import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Channel } from '@shared/types.ts';
import { api } from './api.ts';
import type { LibrarySort } from './sort.ts';

/**
 * Server cache state — kind #1.
 *
 * TanStack Query rather than hand-rolled useEffect fetching. Its caching and
 * stale-while-revalidate is precisely the machinery behind the "no blank
 * screens between transitions" target: navigating back to a screen shows
 * cached data INSTANTLY and refreshes behind it.
 */

export const keys = {
  me: ['me'] as const,
  libraryHome: ['library', 'home'] as const,
  librarySections: ['library', 'sections'] as const,
  libraryBrowse: (section: string, sort: string, desc: boolean) =>
    ['library', 'browse', section, sort, desc] as const,
  libraryItem: (id: string) => ['library', 'item', id] as const,
  libraryChildren: (id: string) => ['library', 'children', id] as const,
  profiles: ['profiles'] as const,
  home: ['home'] as const,
  search: (q: string) => ['search', q] as const,
  requestDetails: (t: string, id: number) => ['request', t, id] as const,
  collection: (id: number) => ['collection', id] as const,
  channels: (kind?: string, filter?: string) =>
    ['channels', kind ?? 'all', filter ?? 'all'] as const,
  groups: (kind: string) => ['groups', kind] as const,
  epg: (id: string) => ['epg', id] as const,
  guide: (bucket: number, kind: string) => ['guide', kind, bucket] as const,
};

/**
 * The guide is fetched in HALF-HOUR BUCKETS, not from "now".
 *
 * Keying the cache on a moving timestamp would make every render a cache miss
 * and every scroll a refetch. Anchoring to the half hour means the whole
 * family shares one cached grid, panning through it costs nothing, and it
 * rolls over on its own without a timer.
 */
export const GUIDE_BUCKET_MS = 30 * 60_000;
export const GUIDE_SPAN_MS = 6 * 60 * 60_000;

export function guideBucket(at = Date.now()): number {
  // Start half an hour back, so whatever is on NOW is always fully visible
  // rather than clipped at the left edge.
  return Math.floor((at - GUIDE_BUCKET_MS) / GUIDE_BUCKET_MS) * GUIDE_BUCKET_MS;
}

export function useMe() {
  return useQuery({
    queryKey: keys.me,
    queryFn: api.me,
    retry: false,          // a 401 is an answer, not a failure to retry
    staleTime: 5 * 60_000,
  });
}

export function useProfiles() {
  return useQuery({ queryKey: keys.profiles, queryFn: api.profiles, retry: false });
}

export interface ChannelFilter {
  group?: string;
  favourites?: boolean;
  /** The hidden ones, and only those — how you get a channel back. */
  hidden?: boolean;
}

/** A stable cache key for a filter, so panning between groups is free. */
function filterKey(f?: ChannelFilter): string {
  if (!f) return 'all';
  return `${f.favourites ? 'fav' : ''}:${f.hidden ? 'hidden' : ''}:${f.group ?? ''}`
    .replace(/^::$/, 'all');
}

export function useChannels(kind?: 'tv' | 'radio', filter?: ChannelFilter) {
  return useQuery({
    queryKey: keys.channels(kind, filterKey(filter)),
    queryFn: () => api.channels({ kind, ...filter }),
    // Channel config changes rarely; don't refetch it on every focus.
    staleTime: 10 * 60_000,
  });
}

/**
 * The library home rows.
 *
 * A SHORT staleTime, unlike channels: Continue Watching changes every time
 * anybody watches anything, and a row that still shows a film you finished
 * last night is the feature visibly not working. Thirty seconds is short
 * enough that coming back from playback shows the truth and long enough that
 * moving between screens does not re-ask.
 */
export function useLibraryHome() {
  return useQuery({
    queryKey: keys.libraryHome,
    queryFn: () => api.libraryHome(),
    staleTime: 30_000,
  });
}

/**
 * The libraries themselves — Movies, Wrestling, Cartoons, TV Shows.
 *
 * Long staleTime: somebody adds a library about once a year, and this is
 * fetched on every visit to the library screen.
 */
export function useLibrarySections() {
  return useQuery({
    queryKey: keys.librarySections,
    queryFn: () => api.librarySections().then((r) => r.sections),
    staleTime: 30 * 60_000,
  });
}

/** How many items to ask Plex for at a time. */
const PAGE = 60;

/**
 * A section, a page at a time.
 *
 * INFINITE RATHER THAN PAGED BUTTONS. A grid you walk with a D-pad has no
 * room for "next page" — you are already holding the button that should mean
 * that — so more arrives as you approach the end and the interaction stays
 * "keep pressing down".
 *
 * The sort is part of the query key, so changing it is a different cached
 * list rather than a refetch of the same one. That means going back to a
 * sort you used a moment ago is instant, which matters on a television where
 * the alternative is watching five thousand posters reload.
 */
export function useLibraryBrowse(
  section: string | undefined, sort: LibrarySort = 'title', desc = false,
) {
  return useInfiniteQuery({
    queryKey: keys.libraryBrowse(section ?? '', sort, desc),
    queryFn: ({ pageParam }) =>
      api.libraryBrowse(section!, { offset: pageParam, limit: PAGE, sort, desc }),
    initialPageParam: 0,
    /*
     * Stop on a SHORT page, not on an empty one.
     *
     * `total` is only sent with the first page, so the last full page would
     * otherwise be followed by one more request that comes back empty — a
     * round trip to Plex per section per visit, to learn something the
     * previous answer already implied.
     */
    getNextPageParam: (last, pages) => {
      const loaded = pages.reduce((n, p) => n + p.items.length, 0);
      if (last.items.length < PAGE) return undefined;
      const total = pages[0]?.total;
      if (total !== undefined && loaded >= total) return undefined;
      return loaded;
    },
    enabled: !!section,
    staleTime: 5 * 60_000,
  });
}

export function useLibraryItem(id: string | undefined) {
  return useQuery({
    queryKey: keys.libraryItem(id ?? ''),
    queryFn: () => api.libraryItem(id!),
    enabled: !!id,
    // Short: the position bar on the detail page has to be right after
    // watching something, and that is the screen people come back to.
    staleTime: 15_000,
  });
}

export function useLibraryChildren(id: string | undefined) {
  return useQuery({
    queryKey: keys.libraryChildren(id ?? ''),
    queryFn: () => api.libraryChildren(id!),
    enabled: !!id,
    staleTime: 15_000,
  });
}

export function useGroups(kind: 'tv' | 'radio') {
  return useQuery({
    queryKey: keys.groups(kind),
    queryFn: () => api.groups(kind),
    staleTime: 10 * 60_000,
  });
}

/**
 * Star or hide a channel.
 *
 * Optimistic, because on a remote the star has to light up on the press. A
 * round-trip before the UI moves reads as a dropped keypress, and the next
 * thing the viewer does is press it again.
 *
 * Every cached channel list is patched, not just the visible one — the same
 * channel appears in the grid, the guide's lineup and the favourites filter,
 * and one of them showing an unstarred star is worse than a slow one.
 */
export function useSetChannelPref() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: { favourite?: boolean; hidden?: boolean } }) =>
      api.setChannelPref(id, patch),

    onMutate: async ({ id, patch }) => {
      await qc.cancelQueries({ queryKey: ['channels'] });
      const snapshot = qc.getQueriesData<Channel[]>({ queryKey: ['channels'] });
      for (const [key, list] of snapshot) {
        if (!list) continue;
        qc.setQueryData<Channel[]>(key, list.map((c) => (c.id === id ? { ...c, ...patch } : c)));
      }
      return { snapshot };
    },

    onError: (_e, _vars, context) => {
      // Put it back. A star that stayed lit after the server refused is a lie
      // the viewer will believe until they next reload.
      for (const [key, list] of context?.snapshot ?? []) qc.setQueryData(key, list);
    },

    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['channels'] });
      void qc.invalidateQueries({ queryKey: ['groups'] });
    },
  });
}

export function useGuide(kind: 'tv' | 'radio' = 'tv') {
  const bucket = guideBucket();
  return useQuery({
    queryKey: keys.guide(bucket, kind),
    queryFn: () => api.guide(new Date(bucket), new Date(bucket + GUIDE_SPAN_MS), kind),
    // A guide does not change under you; refetching it while someone is
    // reading it would move the rows out from under the cursor.
    staleTime: GUIDE_BUCKET_MS,
    gcTime: 2 * GUIDE_SPAN_MS,
  });
}

export function useEpg(channelId: string, enabled = true) {
  return useQuery({
    queryKey: keys.epg(channelId),
    queryFn: () => api.epg(channelId),
    enabled,
    staleTime: 5 * 60_000,
  });
}

/**
 * The profiles this sign-in could switch into.
 *
 * `fresh` is the difference between the two things this answers.
 *
 * The account menu needs to know only WHETHER a "Switch profile" row should
 * exist, which tolerates being a minute out of date. The picker itself is
 * showing actual faces somebody is about to press, and one that was deleted
 * in Plex must not be among them — so it asks again on open, at a moment when
 * somebody has already stopped to choose and a request costs nothing anybody
 * notices.
 *
 * Without that, this cache and the server's sat on top of each other and a
 * removed profile could linger for ten minutes. That does not read as
 * staleness; it reads as the app keeping its own copy of your household that
 * you cannot get rid of.
 *
 * `enabled` on being signed in: asking before that is a guaranteed 401 and a
 * red line in the console on every cold load of the login screen.
 */
export function useHomeProfiles(fresh = false) {
  const { data } = useMe();
  return useQuery({
    queryKey: keys.home,
    queryFn: () => api.home(),
    enabled: !!data?.user,
    staleTime: 60_000,
    refetchOnMount: fresh ? 'always' : true,
  });
}

/**
 * One search across the library, the channels and what could be requested.
 *
 * `enabled` on two characters, because one character matches most of a five
 * thousand film library and every keystroke costs a round trip to Plex AND to
 * Seerr. The debounce that feeds this lives in the page — this is the floor
 * under it, so a paste or a voice search cannot get underneath.
 */
export function useSearch(q: string) {
  return useQuery({
    queryKey: keys.search(q),
    queryFn: () => api.search(q),
    enabled: q.trim().length >= 2,
    staleTime: 60_000,
  });
}

/** Details for one requestable thing, fetched only when somebody opens it. */
export function useRequestDetails(mediaType: 'movie' | 'tv' | null, id: number | null) {
  return useQuery({
    queryKey: keys.requestDetails(mediaType ?? '', id ?? 0),
    queryFn: () => api.requestDetails(mediaType!, id!),
    enabled: !!mediaType && !!id,
    staleTime: 5 * 60_000,
  });
}

/** A film series, fetched only when somebody opens a film that is in one. */
export function useCollection(id: number | null) {
  return useQuery({
    queryKey: keys.collection(id ?? 0),
    queryFn: () => api.collection(id!),
    enabled: !!id,
    staleTime: 5 * 60_000,
  });
}
